import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { UsuarioSessao } from '../../core/session.js';
import type { AuthApi } from './auth.api.js';

/**
 * `<erp-login-page>` — tela de login. Dependências (`authApi`, callback
 * de sucesso) são injetadas via propriedade JS por quem monta a rota
 * (`main.ts`), nunca importadas de um singleton global dentro do
 * componente — mesmo padrão de injeção usado no resto do frontend
 * (`core/router.ts` injeta `Rota.renderizar`, não lê estado global).
 */
export class ErpLoginPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _authApi: AuthApi | null = null;
  private _aoAutenticar: ((usuario: UsuarioSessao) => void) | null = null;
  private enviando = false;
  private mensagemErro = '';
  /** Preservado entre re-renders (ex: após erro) para não obrigar o usuário a redigitar o e-mail; a senha nunca é preservada. */
  private emailDigitado = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set authApi(valor: AuthApi) {
    this._authApi = valor;
  }

  set aoAutenticar(valor: (usuario: UsuarioSessao) => void) {
    this._aoAutenticar = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._authApi || this.enviando) return;

    const email = this.root.querySelector<HTMLInputElement>('#email')?.value.trim() ?? '';
    const senha = this.root.querySelector<HTMLInputElement>('#senha')?.value ?? '';
    this.emailDigitado = email;

    if (!email || !senha) {
      this.mensagemErro = 'informe e-mail e senha';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const usuario = await this._authApi.login(email, senha);
      this._aoAutenticar?.(usuario);
    } catch (erro) {
      this.mensagemErro =
        erro instanceof ApiError ? erro.message : 'não foi possível entrar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: var(--pg-color-bg-base, #121417);
          font-family: var(--pg-font-familia-base, system-ui);
        }
        .cartao {
          width: 100%;
          max-width: 360px;
          padding: var(--pg-space-6, 32px);
        }
        .marca {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          font-weight: var(--pg-font-weight-bold, 700);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin-bottom: var(--pg-space-1, 4px);
        }
        .marca span { color: var(--pg-color-dourado, #c9a24b); }
        .subtitulo {
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          margin-bottom: var(--pg-space-6, 32px);
        }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); }
        ${FORM_FIELD_CSS}
        pg-button { width: 100%; }
        pg-button::part(button) { width: 100%; justify-content: center; }
      </style>
      <pg-card class="cartao">
        <div class="marca">PG <span>Custom</span></div>
        <div class="subtitulo">Entrar no ERP</div>
        <form novalidate>
          <label>
            E-mail
            <input id="email" type="email" autocomplete="username" required value="${escaparHtml(this.emailDigitado)}" />
          </label>
          <label>
            Senha
            <input id="senha" type="password" autocomplete="current-password" required />
          </label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
          <pg-button variant="primary" ${this.enviando ? 'loading' : ''}>Entrar</pg-button>
        </form>
      </pg-card>
    `;

    // `pg-button` não é um controle form-associated (seu <button> real
    // vive na própria shadow root dele) — não existe submit nativo aqui.
    // Enter no campo dispara `submit` normalmente (inputs e <form> estão
    // na mesma shadow root); o clique no botão é tratado via `pg-click`.
    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('pg-button')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-login-page', ErpLoginPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-login-page': ErpLoginPage;
  }
}
